import earcut from 'earcut';
import type { Vec2 } from './vec2';
import { isCounterClockwise } from './polygon';
import { AREA_EPSILON } from '@/constants/numeric';
import { orient2d } from './predicates';

/**
 * Triangulate a simple polygon (no holes) given as counter-clockwise or
 * clockwise vertex indices into a shared coordinate array.
 *
 * Returns triples of indices *into the supplied `loop` array*, always wound
 * counter-clockwise in the pattern plane so downstream normal conventions hold.
 *
 * Uses earcut (ISC licence, Mapbox) for the ear-clipping itself; triangles are
 * re-wound and degenerate ones dropped here.
 */
export function triangulatePolygon(
  coords: readonly Vec2[],
  loop: readonly number[],
): number[][] {
  const n = loop.length;
  if (n < 3) return [];
  if (n === 3) {
    const [a, b, c] = loop;
    if (Math.abs(orient2d(coords[a], coords[b], coords[c])) < AREA_EPSILON) return [];
    return orient2d(coords[a], coords[b], coords[c]) > 0 ? [[a, b, c]] : [[a, c, b]];
  }

  const flat = new Float64Array(n * 2);
  for (let i = 0; i < n; i++) {
    const p = coords[loop[i]];
    flat[i * 2] = p.x;
    flat[i * 2 + 1] = p.y;
  }
  const idx = earcut(Array.from(flat));
  const out: number[][] = [];
  for (let i = 0; i < idx.length; i += 3) {
    const a = loop[idx[i]];
    const b = loop[idx[i + 1]];
    const c = loop[idx[i + 2]];
    const area = orient2d(coords[a], coords[b], coords[c]);
    if (Math.abs(area) < AREA_EPSILON) continue;
    out.push(area > 0 ? [a, b, c] : [a, c, b]);
  }
  return out;
}

/**
 * Fan triangulation about the polygon centroid is *not* used because it adds
 * a vertex; this helper instead reports whether a polygon is convex, which lets
 * callers pick a cheaper fan when possible.
 */
export function isConvexPolygon(coords: readonly Vec2[], loop: readonly number[]): boolean {
  const n = loop.length;
  if (n < 4) return true;
  const ccw = isCounterClockwise(loop.map((i) => coords[i]));
  for (let i = 0; i < n; i++) {
    const a = coords[loop[i]];
    const b = coords[loop[(i + 1) % n]];
    const c = coords[loop[(i + 2) % n]];
    const o = orient2d(a, b, c);
    if (Math.abs(o) < AREA_EPSILON) continue;
    if (ccw ? o < 0 : o > 0) return false;
  }
  return true;
}
